<template>
  <div class="cf-page-container">
    <div
      v-if="receipt.isLoading"
      :class="['cf-container-full-width', { 'cf-page-loader': receipt.isLoading }]"
    >
      <div class="cf-loader" />
    </div>

    <template v-else>
      <div class="cf-card cf-flex cf-flex--column cf-flex--gap-16">
        <!-- Title Container -->
        <div class="cf-flex cf-flex--column cf-flex--align-center cf-flex--gap-8">
          <h1 class="cf-title cf-title--1">
            {{ pageTitle }}
          </h1>

          <receipt-order-fulfillment-time :receipt="receipt" />
        </div>

        <!-- Delivery Receipt Details -->
        <delivery-receipt-fulfillment-details
          v-if="receipt.fulfillmentType === 'Delivery'"
          :receipt="receipt"
        />

        <!-- Shipping Details -->
        <shipping-receipt-fulfillment-details
          v-else-if="receipt.fulfillmentType === 'Shipping'"
          :receipt="receipt"
        />

        <!-- Dine-in Receipt Details -->
        <dine-in-receipt-fulfillment-details
          v-else-if="receipt.fulfillmentType === 'DineIn'"
          :receipt="receipt"
        />

        <!-- Room Service Receipt Details -->
        <room-service-receipt-fulfillment-details
          v-else-if="receipt.fulfillmentType === 'RoomService'"
          :receipt="receipt"
        />

        <!-- Takeout Receipt Details -->
        <takeout-receipt-fulfillment-details
          v-else
          :is-guest-receipt="isGuestReceipt"
          :receipt="receipt"
        />

        <!-- Receipt Qr Code Instructions -->
        <receipt-qr-code-instructions
          v-if="receipt.showRedemptionQrCode"
          :receipt="receipt"
        />

        <!-- Loyalty Receipt Summary -->
        <cf-loyalty-receipt-summary :receipt="receipt" />

        <!-- Create Account Cta -->
        <receipt-create-account-cta
          v-if="isReceiptAccountCreationEnabled"
          :receipt="receipt"
        />

        <!-- Receipt Order Items -->
        <receipt-order-items
          :receipt="receipt"
          :is-guest-receipt="isGuestReceipt"
          :is-item-rating-enabled="isItemRatingEnabled"
        />

        <!-- Contact & Payment Info -->
        <receipt-contact-and-payment-info
          :receipt="receipt"
          :is-guest-receipt="isGuestReceipt"
        />
      </div>

      <!-- Save to Apple/Google Wallets -->
      <save-receipt-to-wallet
        v-if="receipt.googleWalletJwt || receipt.applePassUrl"
        :receipt="receipt"
      />

      <!-- Email Request Receipt Form (which contains app downloads and surveys) -->
      <request-emailed-receipt-form
        v-if="!isGuestReceipt"
        :receipt="receipt"
      />
    </template>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import uaParser from 'ua-parser-js';

  import currentTimestampMixin from '@/mixins/current-timestamp';

  import ReceiptOrderFulfillmentTime from './receipt-order-fulfillment-time/index.vue';
  import TakeoutReceiptFulfillmentDetails from './takeout-receipt-fulfillment-details/index.vue';
  import DeliveryReceiptFulfillmentDetails from './delivery-receipt-fulfillment-details/index.vue';
  import ShippingReceiptFulfillmentDetails from './shipping-receipt-fulfillment-details/index.vue';
  import DineInReceiptFulfillmentDetails from './dine-in-receipt-fulfillment-details/index.vue';
  import RoomServiceReceiptFulfillmentDetails from './room-service-receipt-fulfillment-details/index.vue';
  import ReceiptQrCodeInstructions from './receipt-qr-code-instructions/index.vue';
  import SaveReceiptToWallet from './save-receipt-to-wallet/index.vue';
  import ReceiptCreateAccountCta from './receipt-create-account-cta/index.vue';
  import ReceiptOrderItems from './receipt-order-items/index.vue';
  import ReceiptContactAndPaymentInfo from './receipt-contact-and-payment-info/index.vue';
  import RequestEmailedReceiptForm from './request-emailed-receipt-form/index.vue';

  import CfLoyaltyReceiptSummary from '@/view/components/globals/cf-loyalty-receipt-summary/index.vue';


  export default {
    name: 'OrderReceiptPage',

    components: {
      ReceiptOrderFulfillmentTime,
      TakeoutReceiptFulfillmentDetails,
      DeliveryReceiptFulfillmentDetails,
      ShippingReceiptFulfillmentDetails,
      DineInReceiptFulfillmentDetails,
      RoomServiceReceiptFulfillmentDetails,
      ReceiptQrCodeInstructions,
      SaveReceiptToWallet,
      ReceiptCreateAccountCta,
      ReceiptOrderItems,
      ReceiptContactAndPaymentInfo,
      RequestEmailedReceiptForm,
      CfLoyaltyReceiptSummary
    },

    mixins: [currentTimestampMixin],

    metaInfo() {
      return {
        title: 'Receipt'
      };
    },

    beforeRouteEnter(to, from, next) {
      next((vm) => {
        const isCheckoutFlow = from.name === 'orderMenu' || from.name === 'paymentPage';
        if (isCheckoutFlow) {
          vm.$store.dispatch('merchant/openInAppMessage', { type: 'Receipt' });
        }
      });
    },

    computed: {
      ...mapGetters('content', ['content', 'dynamicContent']),
      ...mapGetters('favoriteOrders', ['favoriteOrders']),
      ...mapGetters('merchant', ['loyaltyOptions', 'merchant']),
      ...mapGetters('receipt', ['receipt', 'yourPayment', 'userLocationType']),
      ...mapGetters('session', ['currentUser']),

      pageTitle() {
        const isCateringMenuType = this.receipt.menuType === 'Catering';

        const isCateringDelivery = this.receipt.fulfillmentType === 'Delivery' && isCateringMenuType;

        if (this.receipt.fulfillmentType === 'DineIn') {
          return this.content.OrderAheadReceipt.metadata.orderAheadDineInReceiptTitle;
        }

        if (this.receipt.fulfillmentType === 'DriveThrough') {
          return this.content.OrderAheadReceipt.metadata.driveThruInfoDescription;
        }

        if (this.receipt.fulfillmentType === 'Curbside') {
          return this.content.OrderAheadReceipt.metadata.curbsideInfoDescription;
        }

        if (this.receipt.fulfillmentType === 'Takeout' && isCateringMenuType) {
          return this.content.OrderAheadReceipt.metadata.cateringInfoDescription;
        }

        if (this.receipt.fulfillmentType === 'Takeout') {
          return this.content.OrderAheadReceipt.metadata.pickUpInfoDescription;
        }

        if (this.receipt.fulfillmentType === 'Shipping') {
          return this.content.OrderAheadReceipt.metadata.shippingInfoDescription;
        }

        if (isCateringDelivery) {
          return this.content.OrderAheadReceipt.metadata.cateringDeliveryInfoDescription;
        }

        if (this.receipt.fulfillmentType === 'Delivery') {
          return this.content.OrderAheadReceipt.metadata.deliveryInfoDescription;
        }

        if (this.receipt.groupOrder.token) {
          return 'Thanks for your group order!';
        }

        return 'Thanks for your order!';
      },

      isGuestReceipt() {
        return !!this.$route.query.guestId;
      },

      isReceiptAccountCreationEnabled() {
        /**
          * For special cases we show loyalty awards on the receipt to non-logged-in users who
          * already have the receipt URI. An order with loyalty awards was placed by a registered
          * user already enrolled in loyalty, so don't show the account creation or enrollment CTA.
        */
        return this.merchant.options.signUpEnabled
          && (
            !this.currentUser.accountId
            || (this.loyaltyOptions.loyaltyEnrollmentEnabled && !this.currentUser.isEnrolledInLoyalty)
          )
          && !this.receipt.loyaltyEngagementSummary;
      },

      isSurveyEnabled() {
        return this.merchant.features['enable-surveys'];
      },

      isItemRatingEnabled() {
        const isItemRatingEnabled = this.merchant.features['item-rating-enabled'] && this.merchant.features['receipt-rating-enabled'];

        return isItemRatingEnabled;
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.loadReceipt();
      },

      loadReceipt() {
        const options = {
          guestId: this.$route.query.guestId,
          orderId: this.$route.query.orderId,
          receiptToken: this.$route.params.receiptToken
        };

        if (this.merchant.options.showOrderQrCode) {
          const { os, browser } = uaParser(window.navigator.userAgent);
          const isMobileSafari = os.name === 'iOS' && /safari/i.test(browser.name);

          options.walletType = isMobileSafari ? 'apple' : 'google';
        }

        this.$store.dispatch('session/updateRouteParamsDirectly', { orderGuid: null, action: null });

        this.$store.dispatch('receipt/getReceipt', options);

        this.$store.dispatch('session/unsetOrder');
      }
    }
  };
</script>
